import { useCallback } from 'react';
import { useTour as useReactTour } from '@reactour/tour';
import { RefCurrentPage } from './NavigationHooks';
import { RefLanguage } from './LanguageHooks';
import { getTourSteps } from '../components/Tour';

export function useTour() {
  const { isOpen, setIsOpen, currentStep, setCurrentStep, setSteps } = useReactTour();
  const currentPage = RefCurrentPage();
  const i18n = RefLanguage();

  const steps = getTourSteps(currentPage, i18n);

  const startTour = useCallback(async () => {
    if (!steps?.length) {
      console.warn('No tour steps available for current page:', currentPage);
      return;
    }
    
    // Scroll to first element and start tour
    const firstElement = document.querySelector(steps[0].selector);
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Small delay to ensure scroll completes
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    setSteps(steps);
    setCurrentStep(0);
    setIsOpen(true);
  }, [currentPage, steps, setSteps, setIsOpen, setCurrentStep]);

  const closeTour = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(0);
  }, [setIsOpen, setCurrentStep]);

  const goToStep = useCallback((stepIndex) => {
    if (steps[stepIndex]) {
      const targetElement = document.querySelector(steps[stepIndex].selector);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => setCurrentStep(stepIndex), 150);
      } else {
        setCurrentStep(stepIndex);
      }
    }
  }, [setCurrentStep, steps]);

  const nextStep = useCallback(() => {
    const nextIndex = currentStep + 1;
    if (nextIndex < steps.length) {
      goToStep(nextIndex);
    } else {
      closeTour();
    }
  }, [currentStep, steps.length, goToStep, closeTour]);

  const prevStep = useCallback(() => {
    const prevIndex = currentStep - 1;
    if (prevIndex >= 0) {
      goToStep(prevIndex);
    }
  }, [currentStep, goToStep]);

  return {
    isTourOpen: isOpen,
    currentStep,
    totalSteps: steps.length,
    startTour,
    closeTour,
    nextStep,
    prevStep,
    goToStep
  };
} 
